// 표준화된 BAR 차트 옵션 생성
import { formatValue } from '../../../utils/numberFormatter';
import { applyCommonStyles } from './applyCommonStyles';

export const generateBarChartOption = (data, baseOption, options = {}, isEditMode) => {
  // 표준 구조: { categories: [...], series: [{ name: "...", values: [...] }] }
  const categories = data.categories || [];
  const series = data.series || [];

  // 차트 크기에 따른 동적 nameGap 계산 (여백 최소화)
  const containerWidth = options.__containerWidth || 500;
  const containerHeight = options.__containerHeight || 400;
  const dynamicYNameGap = containerWidth < 400 ? 35 : (containerWidth < 600 ? 40 : 45);
  const dynamicXNameGap = containerHeight < 300 ? 20 : (containerHeight < 400 ? 25 : 30);

  // 그라데이션 색상 생성 헬퍼 함수
  const createGradient = (color) => {
    // 색상을 밝게 만드는 함수
    const lightenColor = (col, percent) => {
      const num = parseInt(col.replace("#",""), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) + amt;
      const G = (num >> 8 & 0x00FF) + amt;
      const B = (num & 0x0000FF) + amt;
      return "#" + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + (G<255?G<1?0:G:255)*0x100 + (B<255?B<1?0:B:255)).toString(16).slice(1);
    };

    return {
      type: 'linear',
      x: 0, y: 0, x2: 0, y2: 1,
      colorStops: [
        { offset: 0, color: lightenColor(color, 20) },  // 상단 - 밝게
        { offset: 0.5, color: color },  // 중간 - 원색
        { offset: 1, color: lightenColor(color, -15) }  // 하단 - 어둡게
      ],
      global: false
    };
  };
  
  // X축 설정
  baseOption.xAxis = {
    ...baseOption.xAxis,
    type: 'category',
    data: categories,
    name: options.axes?.x || '',
    nameLocation: 'middle',
    nameGap: dynamicXNameGap,
    nameTextStyle: {
      fontSize: 18,
      fontWeight: '600',
      color: '#2c3e50',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }
  };

  // 이중 Y축 지원
  const hasDualYAxis = options.advanced?.dualYAxis && typeof options.advanced.dualYAxis === 'object';
  
  if (hasDualYAxis) {
    console.log('[BAR Chart] 이중 Y축 설정 감지:', options.advanced.dualYAxis);
    console.log('[BAR Chart] secondaryAxisSeries:', options.advanced?.secondaryAxisSeries);
    
    // secondaryAxisSeries가 없고 시리즈가 2개인 경우 자동으로 두 번째 시리즈를 보조축에 할당
    if (!options.advanced?.secondaryAxisSeries && series.length === 2) {
      console.log('[BAR Chart] secondaryAxisSeries가 없어서 두 번째 시리즈를 자동으로 보조축에 할당');
      if (!options.advanced) options.advanced = {};
      options.advanced.secondaryAxisSeries = [series[1].name];
    }
    // 이중 Y축 설정
    baseOption.yAxis = [
      {
        type: 'value',
        name: options.advanced.dualYAxis.primary?.name || options.axes?.y || '',
        position: 'left',
        nameLocation: 'middle',
        nameGap: dynamicYNameGap,
        nameTextStyle: {
          fontSize: 18,
          fontWeight: '600',
          color: options.advanced.dualYAxis.primary?.color || '#5470c6',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: options.advanced.dualYAxis.primary?.color || '#5470c6',
            width: 2
          }
        },
        axisLabel: {
          show: true,
          color: options.advanced.dualYAxis.primary?.color || '#5470c6',
          fontSize: 14,
          formatter: function(value) {
            return formatValue(value);
          }
        },
        axisTick: {
          show: true,
          lineStyle: {
            color: options.advanced.dualYAxis.primary?.color || '#5470c6'
          }
        },
        min: options.advanced.dualYAxis.primary?.min,
        max: options.advanced.dualYAxis.primary?.max,
        interval: options.advanced.dualYAxis.primary?.interval,
        scale: options.advanced.dualYAxis.primary?.scale !== undefined ? options.advanced.dualYAxis.primary.scale : false
      },
      {
        type: 'value',
        show: true,
        name: options.advanced.dualYAxis.secondary?.name || '',
        position: 'right',
        offset: 0,
        nameLocation: 'middle',
        nameGap: dynamicYNameGap,
        nameTextStyle: {
          fontSize: 18,
          fontWeight: '600',
          color: options.advanced.dualYAxis.secondary?.color || '#91cc75',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: options.advanced.dualYAxis.secondary?.color || '#91cc75',
            width: 2
          }
        },
        axisLabel: {
          show: true,
          color: options.advanced.dualYAxis.secondary?.color || '#91cc75',
          fontSize: 14,
          formatter: function(value) {
            return formatValue(value);
          }
        },
        axisTick: {
          show: true,
          lineStyle: {
            color: options.advanced.dualYAxis.secondary?.color || '#91cc75'
          }
        },
        splitLine: {
          show: false
        },
        min: options.advanced.dualYAxis.secondary?.min,
        max: options.advanced.dualYAxis.secondary?.max,
        interval: options.advanced.dualYAxis.secondary?.interval,
        scale: options.advanced.dualYAxis.secondary?.scale !== undefined ? options.advanced.dualYAxis.secondary.scale : true
      }
    ];
    
    // containLabel: true가 양쪽 Y축 제목 길이를 자동으로 측정하여 여백을 계산하므로
    // 하드코딩된 grid.left/right 설정은 제거 (근본적 해결)
  } else {
    // 단일 Y축 설정
    baseOption.yAxis = {
      ...baseOption.yAxis,
      type: 'value',
      name: options.axes?.y || '',
      nameLocation: 'middle',
      nameGap: dynamicYNameGap,
      nameTextStyle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#2c3e50',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      },
      axisLabel: {
        formatter: function(value) {
          return formatValue(value);
        }
      },
      // 고급 옵션 지원
      min: options.advanced?.yMin,
      max: options.advanced?.yMax,
      interval: options.advanced?.yInterval,
      scale: options.advanced?.yScale !== undefined ? options.advanced.yScale : false
    };
  }

  // containLabel: true가 Y축 제목 길이를 자동으로 측정하여 여백을 계산하므로
  // 하드코딩된 grid.left 설정은 제거 (근본적 해결)

  // 기본 색상 배열 (회색 계열 그라데이션 + 다양한 색상)
  const defaultGradientColors = [
    '#37474F', '#455A64', '#546E7A', '#607D8B', '#78909C', 
    '#90A4AE', '#B0BEC5', '#CFD8DC', '#ECEFF1', '#F5F5F5'
  ];
  
  const defaultBrightColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FCEA2B',
    '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
    '#FF6348', '#2ED573', '#3742FA', '#FF3838', '#2F3542'
  ];

  // 색상 설정 확인 (대시보드: options.colors, 채팅: options.style.colors)
  const userColors = options.colors || options.style?.colors;
  
  // 색상이 지정되지 않았을 때는 회색 그라데이션, 지정되었을 때는 사용자 색상 또는 기본 다양한 색상
  const colors = userColors || defaultGradientColors;
  
  const shouldUseVariedColors = series.length === 1 && (!userColors || userColors.length > 1);

  // 시리즈 설정 (개선된 색상 처리)
  // 데이터 라벨 표시 설정 및 포맷터
  const showDataLabels = options.advanced?.showDataLabels === true;
  const labelFormat = options.advanced?.labelFormat || '{value}';
  const unitText = options.unit || '';

  const makeBarLabelFormatter = () => (params) => {
    const rawValue = typeof params.value === 'object' && params.value !== null && params.value.value !== undefined
      ? params.value.value
      : params.value;
    const formatted = formatValue(rawValue);
    return labelFormat
      .replace('{name}', params.name ?? '')
      .replace('{seriesName}', params.seriesName ?? '')
      .replace('{value}', formatted)
      .replace('{unit}', unitText);
  };

  // 시리즈 개수에 따른 바 너비 동적 조정 (그룹화를 위해 다시 조정)
  const barWidth = undefined;  // ECharts가 자동으로 계산하도록 설정

  baseOption.series = series.map((seriesItem, seriesIndex) => {
    // secondaryAxisSeries 배열 체크
    const secondaryAxisSeries = options.advanced?.secondaryAxisSeries || [];
    const useSecondaryAxis = seriesItem.useSecondaryAxis || secondaryAxisSeries.includes(seriesItem.name);

    if (hasDualYAxis && useSecondaryAxis) {
      console.log(`[BAR Chart] 시리즈 "${seriesItem.name}"이 보조 Y축 사용`);
    }

    // 첫 번째 시리즈에만 barGap과 barCategoryGap 설정 (다중 시리즈일 때)
    const barGapSettings = {};
    if (series.length > 1 && seriesIndex === 0) {
      barGapSettings.barGap = '0%';  // 같은 카테고리 내 시리즈 간 간격 없음 (완전히 붙어있음)
      barGapSettings.barCategoryGap = '20%';  // 카테고리 간 간격 (적절히 띄우기)
    }

    // 단일 시리즈인 경우: 첫 번째 색상 하나로 통일
    if (series.length === 1) {
      {
        // 개별 아이템에 포함된 itemStyle.color 제거 (시리즈 색상 우선 적용)
        const cleanedData = (seriesItem.values || []).map(v => {
          if (typeof v === 'object' && v !== null && v.value !== undefined) {
            const { itemStyle, ...rest } = v;
            return rest;
          }
          return v;
        });
        // 단일 색상으로 통일
        return {
          name: seriesItem.name || '데이터',
          type: 'bar',
          data: cleanedData,
          ...barGapSettings,  // barGap과 barCategoryGap 설정 추가
          silent: false,  // 클릭 이벤트 활성화
          itemStyle: {
            color: createGradient((userColors && userColors[0]) || colors[0]),
            borderRadius: [8, 8, 0, 0],  // 상단만 둥글게
            shadowColor: 'rgba(0, 0, 0, 0.2)',
            shadowBlur: 4,
            shadowOffsetY: 2
          },
          label: {
            show: showDataLabels,
            position: options.advanced?.labelPosition || 'top',
            formatter: makeBarLabelFormatter()
          },
          // 이중 Y축 지원: useSecondaryAxis 속성에 따라 Y축 인덱스 결정
          yAxisIndex: hasDualYAxis && useSecondaryAxis ? 1 : 0,
          emphasis: {
            focus: 'series',
            itemStyle: {
              shadowColor: 'rgba(0, 0, 0, 0.4)',
              shadowBlur: 10,
              shadowOffsetY: 4
            }
          }
        };
      }
    } else {
      // 다중 시리즈: 기존 방식 (시리즈별로 하나의 색상)
      return {
        name: seriesItem.name || '데이터',
        type: 'bar',
        data: (seriesItem.values || []).map((v) => (typeof v === 'object' && v !== null && v.value !== undefined) ? { value: v.value, __row: v.__row, __cols: v.__cols, __extras: v.__extras } : v),
        barWidth: barWidth,
        ...barGapSettings,  // barGap과 barCategoryGap 설정 추가
        itemStyle: {
          color: createGradient(colors[seriesIndex % colors.length]),
          borderRadius: [8, 8, 0, 0],  // 상단만 둥글게
          shadowColor: 'rgba(0, 0, 0, 0.2)',
          shadowBlur: 4,
          shadowOffsetY: 2
        },
        label: {
          show: showDataLabels,
          position: options.advanced?.labelPosition || 'top',
          formatter: makeBarLabelFormatter()
        },
        // 이중 Y축 지원: useSecondaryAxis 속성에 따라 Y축 인덱스 결정
        yAxisIndex: hasDualYAxis && useSecondaryAxis ? 1 : 0,
        emphasis: {
          focus: 'series',
          itemStyle: {
            shadowColor: 'rgba(0, 0, 0, 0.4)',
            shadowBlur: 10,
            shadowOffsetY: 4
          }
        }
      };
    }
  });

  // 전체 색상 팔레트 설정 (단일 시리즈에서 다양한 색상을 사용할 때는 설정하지 않음)
  if (!shouldUseVariedColors) {
    baseOption.color = colors;
  }

  // 범례 설정 - showLegend 옵션이 true이거나 다중 시리즈인 경우 표시
  const showLegend = options.advanced?.showLegend === true || series.length > 1;
  
  if (showLegend || options.advanced?.legendPosition) {
    // 범례 위치 옵션 처리
    const legendPosition = options.advanced?.legendPosition || 'bottom';
    
    // 범례 순서 설정 (legendOrder가 있으면 해당 순서로, 없으면 series 순서대로)
    let legendData;
    if (options.advanced?.legendOrder && Array.isArray(options.advanced.legendOrder)) {
      // 사용자 지정 순서가 있는 경우
      const seriesMap = new Map(series.map(s => [s.name || '데이터', s]));
      legendData = options.advanced.legendOrder.filter(name => seriesMap.has(name));
      // legendOrder에 없는 시리즈들은 뒤에 추가
      const remainingSeries = series.filter(s => !options.advanced.legendOrder.includes(s.name || '데이터'));
      legendData = [...legendData, ...remainingSeries.map(s => s.name || '데이터')];
    } else {
      // 기본: series 순서대로
      legendData = series.map(s => s.name || '데이터');
    }
    
    const legendConfig = {
      data: legendData,
      type: 'scroll'
    };

    // 위치별 설정 - 각 케이스에서 모든 방향 속성을 명시적으로 설정
    switch(legendPosition) {
      case 'top':
        legendConfig.top = 10;
        legendConfig.bottom = undefined;
        legendConfig.left = 'center';
        legendConfig.right = undefined;
        legendConfig.orient = 'horizontal';
        break;
      case 'bottom':
        legendConfig.top = undefined;
        legendConfig.bottom = 10;
        legendConfig.left = 'center';
        legendConfig.right = undefined;
        legendConfig.orient = 'horizontal';
        break;
      case 'left':
        legendConfig.top = 'middle';
        legendConfig.bottom = undefined;
        legendConfig.left = 10;
        legendConfig.right = undefined;
        legendConfig.orient = 'vertical';
        break;
      case 'right':
        legendConfig.top = 'middle';
        legendConfig.bottom = undefined;
        legendConfig.left = undefined;
        legendConfig.right = 10;
        legendConfig.orient = 'vertical';
        break;
      case 'top-right':
        legendConfig.top = '5%';
        legendConfig.right = '5%';
        legendConfig.orient = 'vertical';
        legendConfig.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        legendConfig.borderColor = '#ddd';
        legendConfig.borderWidth = 1;
        legendConfig.borderRadius = 4;
        legendConfig.padding = [5, 10];
        legendConfig.itemGap = 5;
        break;
      case 'top-left':
        legendConfig.top = '5%';
        legendConfig.left = '5%';
        legendConfig.orient = 'vertical';
        legendConfig.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        legendConfig.borderColor = '#ddd';
        legendConfig.borderWidth = 1;
        legendConfig.borderRadius = 4;
        legendConfig.padding = [5, 10];
        legendConfig.itemGap = 5;
        break;
    }

    baseOption.legend = legendConfig;

    // 범례 위치에 따른 grid 여백 조정 (범례와 차트 영역 겹침 방지)
    if (legendPosition === 'right') {
      baseOption.grid = { ...baseOption.grid, right: (baseOption.grid?.right || 30) + 80 };
    } else if (legendPosition === 'left') {
      baseOption.grid = { ...baseOption.grid, left: (baseOption.grid?.left || 50) + 80 };
    } else if (legendPosition === 'top' || legendPosition === 'top-left' || legendPosition === 'top-right') {
      baseOption.grid = { ...baseOption.grid, top: (baseOption.grid?.top || 30) + 30 };
    }
  } else {
    delete baseOption.legend;
  }

  // 가로 막대 차트 옵션
  if (options.advanced?.horizontal) {
    // 축 교환
    const tempXAxis = { ...baseOption.xAxis };
    const tempYAxis = baseOption.yAxis;
    
    // 이중 축인 경우와 단일 축인 경우를 구분하여 처리
    if (hasDualYAxis) {
      // 이중 X축 (가로 막대에서는 값이 X축)
      baseOption.xAxis = [
        {
          ...tempYAxis[0],
          type: 'value',
          position: tempYAxis[0].position === 'left' ? 'bottom' : 'top',
          nameLocation: 'middle',
          nameGap: 40
        },
        {
          ...tempYAxis[1],
          type: 'value',
          position: tempYAxis[1].position === 'right' ? 'top' : 'bottom',
          nameLocation: 'middle',
          nameGap: 40
        }
      ];
      
      // containLabel: true가 자동으로 여백을 계산하므로 하드코딩 제거
      
      // 가로 막대에서는 축이 바뀌므로 시리즈의 축 인덱스도 변경
      baseOption.series = baseOption.series.map(s => ({
        ...s,
        xAxisIndex: s.yAxisIndex,
        yAxisIndex: 0
      }));
    } else {
      baseOption.xAxis = {
        ...tempYAxis,
        type: 'value',
        name: options.axes?.y || '',
        nameLocation: 'middle',
        nameGap: 40
      };
    }
    
    baseOption.yAxis = {
      ...tempXAxis,
      type: 'category',
      data: categories,
      name: options.axes?.x || '',
      nameLocation: 'middle',
      nameGap: 130,
      nameTextStyle: {
        ...tempXAxis.nameTextStyle,
        align: 'left',
        verticalAlign: 'middle'
      }
    };
    
    // containLabel: true가 자동으로 여백을 계산하므로 하드코딩 제거
  }

  // containLabel: true가 범례와 축 제목을 포함한 모든 요소의 크기를 자동으로 측정하여
  // 최적의 grid 영역을 계산하므로 하드코딩된 grid.bottom 설정은 제거 (근본적 해결)

  // 디버깅을 위한 최종 옵션 출력
  if (hasDualYAxis) {
    console.log('[BAR Chart] 최종 차트 옵션:', {
      yAxis: baseOption.yAxis,
      series: baseOption.series.map(s => ({ name: s.name, yAxisIndex: s.yAxisIndex }))
    });
  }

  // 툴팁 순서 설정 (legendOrder와 동일하게)
  if (options.advanced?.legendOrder && Array.isArray(options.advanced.legendOrder) && series.length > 1) {
    baseOption.tooltip = {
      ...baseOption.tooltip,
      formatter: function(params) {
        if (!Array.isArray(params)) params = [params];
        
        let tooltipHtml = '';
        if (params[0] && params[0].name) {
          tooltipHtml += `${params[0].name}<br/>`;
        }
        
        // legendOrder 순서대로 정렬
        const seriesMap = new Map(series.map(s => [s.name || '데이터', s]));
        const orderedSeriesNames = options.advanced.legendOrder.filter(name => seriesMap.has(name));
        const remainingSeriesNames = series.filter(s => !options.advanced.legendOrder.includes(s.name || '데이터')).map(s => s.name || '데이터');
        const allOrderedNames = [...orderedSeriesNames, ...remainingSeriesNames];
        
        // 순서대로 툴팁 항목 생성
        allOrderedNames.forEach(seriesName => {
          const param = params.find(p => p.seriesName === seriesName);
          if (param) {
            const value = typeof param.value === 'object' && param.value !== null && param.value.value !== undefined
              ? param.value.value
              : param.value;
            const formattedValue = formatValue(value);
            tooltipHtml += `${param.marker}${param.seriesName}: <b>${formattedValue}</b><br/>`;
          }
        });
        
        return tooltipHtml;
      }
    };
  }

  // 공통 스타일 적용
  if (options.style) {
    baseOption = applyCommonStyles(baseOption, options.style);
  }

  // 사용자가 advanced.grid로 직접 여백을 지정한 경우 최종 오버라이드
  if (options.advanced?.grid && typeof options.advanced.grid === 'object') {
    baseOption.grid = { ...baseOption.grid, ...options.advanced.grid };
  }

  // transparent 처리 (ECharts 차트 배경 투명도)
  // showFrame은 DashboardComponentRenderer에서 처리하므로 여기서는 transparent만 처리
  // transparent 옵션 체크 (boolean true 또는 string "true" 모두 처리)
  const isTransparent = options.transparent === true || options.transparent === 'true';
  
  if (isTransparent) {
    // ECharts 전체 배경과 그리드 배경을 모두 투명하게 설정
    baseOption.backgroundColor = 'transparent';
    if (baseOption.grid) {
      baseOption.grid.backgroundColor = 'transparent';
    }
  }

  return baseOption;
}; 